import styled from '@emotion/styled';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { CircleUserRound, Shield, Trophy } from 'lucide-react';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React from 'react';
import type { MessageDescriptor } from 'shared/i18n';
import { Decor, Hot, Logo, Poster, PosterHeader, SchemeTable, Subtitle, Title, Titles, useKeyText } from './scheme-parts';

const STAT_COLUMNS: readonly { key: PlaceholderKey; heading: MessageDescriptor }[] = [
  { key: PlaceholderKey.PLAYER_GAMES_PLAYED, heading: msg`И` },
  { key: PlaceholderKey.PLAYER_GOALS, heading: msg`Г` },
  { key: PlaceholderKey.PLAYER_ASSISTS, heading: msg`Пас` },
  { key: PlaceholderKey.PLAYER_GOALS_ASSISTS, heading: msg`Г+П` },
  { key: PlaceholderKey.PLAYER_YELLOW_CARDS, heading: msg`ЖК` },
  { key: PlaceholderKey.PLAYER_RED_CARDS, heading: msg`КК` },
];

/**
 * TOP_PLAYERS_BY_INDICATORS: a leaderboard. The keys sit in the first place of
 * the ranking; the team keys read the team of that player.
 */
const PlayerStatsScheme: React.FC = () => {
  const { t } = useLingui();
  const text = useKeyText();
  const photo = (
    <Logo round>
      <CircleUserRound strokeWidth={1.5} />
    </Logo>
  );
  const shield = (
    <Logo round>
      <Shield strokeWidth={1.5} />
    </Logo>
  );
  return (
    <Poster>
      <PosterHeader>
        <Hot k={PlaceholderKey.TOURNAMENT_LOGO}>
          <Logo>
            <Trophy strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Titles>
          <Hot k={PlaceholderKey.CHAMPIONSHIP_NAME}>
            <Title>{text(PlaceholderKey.CHAMPIONSHIP_NAME)}</Title>
          </Hot>
          <Hot k={PlaceholderKey.TOURNAMENT_NAME}>
            <Subtitle>{text(PlaceholderKey.TOURNAMENT_NAME)}</Subtitle>
          </Hot>
        </Titles>
      </PosterHeader>
      <SchemeTable>
        <thead>
          <tr>
            <th>#</th>
            <th className="left">{t`Игрок`}</th>
            <th className="left">{t`Команда`}</th>
            {STAT_COLUMNS.map(({ key, heading }) => (
              <th key={key}>{t(heading)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Decor>1</Decor>
            </td>
            <td className="left">
              <Cell>
                <Hot k={PlaceholderKey.PLAYER_PHOTO}>{photo}</Hot>
                <Lines>
                  <Hot k={PlaceholderKey.PLAYER_NAME}>
                    <b>{text(PlaceholderKey.PLAYER_NAME)}</b>
                  </Hot>
                  <Cell>
                    <Hot k={PlaceholderKey.PLAYER_NUMBER}>{text(PlaceholderKey.PLAYER_NUMBER)}</Hot>
                    <Hot k={PlaceholderKey.PLAYER_POSITION}>{text(PlaceholderKey.PLAYER_POSITION)}</Hot>
                  </Cell>
                </Lines>
              </Cell>
            </td>
            <td className="left">
              <Cell>
                <Hot k={PlaceholderKey.TEAM_LOGO}>{shield}</Hot>
                <Lines>
                  <Hot k={PlaceholderKey.TEAM_NAME}>{text(PlaceholderKey.TEAM_NAME)}</Hot>
                  <Hot k={PlaceholderKey.TEAM_SHORT_NAME}>{text(PlaceholderKey.TEAM_SHORT_NAME)}</Hot>
                </Lines>
              </Cell>
            </td>
            {STAT_COLUMNS.map(({ key }) => (
              <td key={key}>
                <Hot k={key}>{text(key)}</Hot>
              </td>
            ))}
          </tr>
          {[1, 2].map((row) => (
            <tr key={row}>
              <td>
                <Decor>{row + 1}</Decor>
              </td>
              <td className="left">
                <Cell>
                  {photo}
                  <Decor>{text(PlaceholderKey.PLAYER_NAME, row)}</Decor>
                </Cell>
              </td>
              <td className="left">
                <Cell>
                  {shield}
                  <Decor>{text(PlaceholderKey.TEAM_SHORT_NAME, row)}</Decor>
                </Cell>
              </td>
              {STAT_COLUMNS.map(({ key }) => (
                <td key={key}>
                  <Decor>{text(key, row)}</Decor>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </SchemeTable>
    </Poster>
  );
};

export default PlayerStatsScheme;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;
